import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Whether the delivery scheduler's required fields (buyer name/WhatsApp, recipient
   * name/phone, address, date, time slot) are complete — gates the checkout button.
   * Initialized from the checkout button's own `data-delivery-ready` attribute (a
   * server-rendered snapshot of cart attributes from a previous visit, see
   * snippets/cart-summary.liquid), then kept live via the delivery-scheduler-component's
   * `delivery-scheduler:completeness-change` event.
   * @type {boolean}
   */
  #deliveryComplete = false;

  connectedCallback() {
    super.connectedCallback();

    this.#deliveryComplete = this.refs.checkoutButton?.dataset.deliveryReady === 'true';

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    document.addEventListener('delivery-scheduler:completeness-change', this.#handleDeliveryCompletenessChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    document.removeEventListener('delivery-scheduler:completeness-change', this.#handleDeliveryCompletenessChange);
  }

  /** @param {CustomEvent<{complete: boolean}>} event */
  #handleDeliveryCompletenessChange = (event) => {
    this.#deliveryComplete = event.detail.complete;
    this.#updateCheckoutButtonState();
  };

  /**
   * On the cart PAGE, this is a hard block: the button's own `disabled` property is set
   * directly (a real DOM property, not a CSS/visual-only state — a disabled submit button
   * cannot be clicked or submit its form, full stop).
   *
   * The cart DRAWER never mounts the delivery-scheduler (it's cart-page-only), so there's no
   * live per-keystroke state to disable against there — instead of trying to duplicate a
   * second disabled-button implementation that would only ever reflect the last full-page
   * snapshot, the drawer's button stays clickable and handleCheckoutClick() below intercepts
   * the click and redirects to /cart when incomplete. One mechanism to keep in sync (this
   * flag) instead of two (a live disabled state in a place that can't validate anything).
   */
  #updateCheckoutButtonState() {
    const { checkoutButton } = this.refs;
    if (!checkoutButton || this.isDrawer) return;

    // An add-ons-only cart (every line carries `_addon_line` — wine, balloons, etc. with no
    // real gift) must stay blocked even once every delivery field is filled. `data-has-product`
    // is a server-rendered fact refreshed on every cart morph (see snippets/cart-summary.liquid);
    // it can only change on a cart-line change, which always re-renders this section, so reading
    // it here is always current. Missing/absent attribute is treated as "has product" so this
    // never regresses an ordinary cart.
    const hasProduct = checkoutButton.dataset.hasProduct !== 'false';

    checkoutButton.disabled = !this.#deliveryComplete || !hasProduct;
  }

  /**
   * On the cart PAGE, this only ever fires once delivery is complete (the button is hard
   * `disabled` otherwise), so it always builds a pre-filled checkout URL from the delivery
   * scheduler's live input values. In the DRAWER, an incomplete cart still redirects to /cart
   * as before; a complete one now also gets the pre-filled URL, sourced from the cart's saved
   * attributes (via fetchCartData()) since the drawer never mounts the scheduler's inputs.
   * @param {MouseEvent} event
   */
  async handleCheckoutClick(event) {
    if (this.isDrawer && !this.#deliveryComplete) {
      event.preventDefault();
      window.location.href = Theme.routes.cart_url;
      return;
    }

    event.preventDefault();

    const recipient = this.isDrawer ? await this.#getRecipientFromCart() : this.#getRecipientFromInputs();
    window.location.href = this.#buildCheckoutUrl(recipient);
  }

  /** @returns {{name: string, phone: string, address: string}} */
  #getRecipientFromInputs() {
    const nameInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="recipientNameInput"]'));
    const phoneInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="recipientPhoneInput"]'));
    const addressInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="addressInput"]'));

    return {
      name: nameInput?.value.trim() ?? '',
      phone: phoneInput?.value.trim() ?? '',
      address: addressInput?.value.trim() ?? '',
    };
  }

  /** @returns {Promise<{name: string, phone: string, address: string}>} */
  async #getRecipientFromCart() {
    try {
      const cart = await this.fetchCartData();
      return {
        name: cart.attributes?.['Recipient Name'] ?? '',
        phone: cart.attributes?.['Recipient Phone'] ?? '',
        address: cart.attributes?.['Delivery Address'] ?? '',
      };
    } catch (error) {
      console.warn('[cart-items] Failed to fetch cart attributes for checkout prefill:', error);
      return { name: '', phone: '', address: '' };
    }
  }

  /**
   * Builds a /checkout URL with the recipient's details pre-filled via Shopify's cart
   * permalink query parameters, so the customer doesn't have to retype what they already
   * entered in "Who is this gift for?". first_name/last_name/address1 are officially
   * documented: https://shopify.dev/docs/apps/build/checkout/create-cart-permalinks.
   * shipping_address[phone] is NOT in that documented set — it's included anyway since
   * Shopify's checkout honors it in practice, but unlike the others it isn't guaranteed.
   * @param {{name: string, phone: string, address: string}} recipient
   * @returns {string}
   */
  #buildCheckoutUrl({ name, phone, address }) {
    const url = new URL(Theme.routes.checkout_url, window.location.origin);
    const [firstName = '', ...rest] = name.split(/\s+/).filter(Boolean);
    const lastName = rest.join(' ');

    if (firstName) url.searchParams.set('checkout[shipping_address][first_name]', firstName);
    if (lastName) url.searchParams.set('checkout[shipping_address][last_name]', lastName);
    if (address) url.searchParams.set('checkout[shipping_address][address1]', address);
    if (phone) url.searchParams.set('checkout[shipping_address][phone]', phone);

    return `${url.pathname}${url.search}`;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    // Resolve the row from the control the shopper actually interacted with, NOT from
    // `line` (a canonical cart index): rows render price-descending in cart-products.liquid,
    // so `this.refs.cartItemRows[line - 1]` points at a different line — see onLineItemRemove.
    const lineItemRow =
      (event.target instanceof Element ? event.target.closest('[data-key]') : null) ??
      this.refs.cartItemRows[line - 1];

    if (quantity === 0) {
      return this.onLineItemRemove(lineItemRow ?? line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      key: lineItemRow instanceof HTMLElement ? lineItemRow.dataset.key : undefined,
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Resolves a cart line row (`<tr data-key>`) from whatever the caller has:
   *   - an `Event` — use its real target (`event.target`, which the Component framework's
   *     delegated listener already points at the `[on:click]` element) and walk up to the row;
   *   - the row `HTMLElement` itself (or any descendant of one);
   *   - a 1-based DOM position into `this.refs.cartItemRows`, kept ONLY as a legacy fallback.
   * The positional form must never be fed a canonical cart index (`item.index`) — the two
   * orderings diverge under the price-descending render sort. See onLineItemRemove.
   * @param {Event | HTMLElement | number | null | undefined} source
   * @returns {HTMLElement | undefined}
   */
  #resolveCartItemRow(source) {
    if (source instanceof Event) {
      const target = source.target;
      const row = target instanceof Element ? target.closest('[data-key]') : null;
      return row instanceof HTMLElement ? row : undefined;
    }
    if (source instanceof HTMLElement) {
      const row = source.matches('[data-key]') ? source : source.closest('[data-key]');
      return row instanceof HTMLElement ? row : undefined;
    }
    if (typeof source === 'number') {
      const row = this.refs.cartItemRows?.[source - 1];
      return row instanceof HTMLElement ? row : undefined;
    }
    return undefined;
  }

  /**
   * Handles the line item removal — cascades to also remove any personalized card/ribbon line
   * tied to this same product, so deleting a bouquet doesn't leave its card/ribbon behind as an
   * orphaned line the customer has to find and delete separately. The companion lines' keys are
   * read straight off this row's own `data-card-line-key`/`data-ribbon-line-key` attributes
   * (written by the same Liquid pass that computes them in snippets/cart-products.liquid, on
   * every cart morph) rather than fetched — no extra network round trip before the removal
   * starts, and no drift risk since Liquid recomputes them fresh every render.
   * The row to delete is resolved from the element the shopper actually clicked (or an
   * explicitly-passed row element), NEVER from a positional index. cart-products.liquid
   * renders rows sorted price-descending, so a line's canonical cart index (`item.index`,
   * the old `on:click` arg) does not line up with its position in `this.refs.cartItemRows`
   * (DOM order) — indexing one array by the other deletes whichever line now sits at that
   * position. Confirmed live: clicking delete on line 2 (canonical) removed the row at DOM
   * position 2, a different product. Removal is by the resolved row's own stable `data-key`.
   * @param {Event | HTMLElement | number} source - the click event, the row element, or (legacy) a 1-based DOM position
   */
  onLineItemRemove(source) {
    // A cart mutation is already in flight (the subtree is pointer-events:none, so a real
    // click can't get here — this guards programmatic/queued calls). Mirrors clearCart().
    if (this.classList.contains('cart-items-disabled')) return;

    const cartItemRowToRemove = this.#resolveCartItemRow(source);

    if (!cartItemRowToRemove) return;

    const companionKeys = [
      cartItemRowToRemove.dataset.cardLineKey,
      cartItemRowToRemove.dataset.ribbonLineKey,
      cartItemRowToRemove.dataset.liliesLineKey,
      cartItemRowToRemove.dataset.matchingBouquetLineKey,
      cartItemRowToRemove.dataset.vaseLineKey,
    ].filter((key) => !!key);

    this.#removeLines([cartItemRowToRemove.dataset.key ?? '', ...companionKeys]);

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      // Get the card/ribbon/lilies/vase/matching-bouquet rows cascading off this removal (a
      // card line never has its own row — see cart-products.liquid — so only a matching
      // ribbon/lilies/vase/matching-bouquet row is ever actually found here)
      ...this.refs.cartItemRows.filter((row) => companionKeys.includes(row.dataset.key ?? '')),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Removes one or more duplicate/extra add-on lines (card/ribbon/lilies/vase/matching-bouquet)
   * in a single tap — the defense-in-depth safety net for that class of bug (see
   * assets/product-personalization.js's #syncCardWithCart doc comment for the actual root-cause
   * fix, and snippets/cart-line-duplicate-addon-warning-row.liquid for where this is called
   * from). Keys come straight off the clicked button's own `data-duplicate-keys` attribute
   * (pipe-delimited) — written fresh by cart-products.liquid's duplicate-detection scan on
   * every cart morph, so it's never a stale copy — and removed via the same atomic
   * #removeLines used by onLineItemRemove.
   * @param {Event} event
   */
  removeDuplicateAddonLines(event) {
    if (this.classList.contains('cart-items-disabled')) return;

    const target = event.target instanceof Element ? event.target.closest('[data-duplicate-keys]') : null;
    if (!(target instanceof HTMLElement)) return;

    const keys = (target.dataset.duplicateKeys ?? '').split('|').filter(Boolean);
    this.#removeLines(keys);
  }

  /**
   * Clears every line item from the cart in one call, after a confirmation prompt so it
   * can't be triggered by an accidental click. Mirrors updateQuantity()'s fetch → morph →
   * CartLinesUpdateEvent flow (disable, shimmer, dispatch, morphSection, re-enable) via
   * /cart/clear.js instead of /cart/change.js, so the header cart badge and every other
   * cart-items-component on the page (drawer + page, if both are present) stay in sync.
   */
  clearCart() {
    if (this.classList.contains('cart-items-disabled')) return;
    if (!window.confirm('Clear all items from your cart?')) return;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: (this.refs.cartItemRows ?? []).map((row) => ({ id: row.dataset.key ?? '', quantity: 0 })),
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_clear_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          this.dispatchEvent(
            new CartErrorEvent({ error: parsedResponseText.errors, code: 'INVALID' })
          );
          return;
        }

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: 0,
            source: 'cart-items-component',
            didError: false,
          },
        });

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to clear cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line's 1-based canonical cart position (correct for the
   *   `/cart/change.js` `line` param — that API indexes `cart.items`, not display order).
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.key] - The line's stable cart key, resolved from the clicked
   *   control. Used for the outbound `CartLinesUpdateEvent` so listeners (cart drawer sync,
   *   analytics) get the line the shopper actually changed, not whatever sits at
   *   `cartItemRows[line - 1]` in the price-sorted DOM.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = config.key ?? this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Removes one or more cart lines in a single atomic `/cart/update.js` call, keyed by each
   * line's own cart `key` rather than its numeric position — critical here since removing a
   * product together with its card/ribbon companions in one request means their positions in
   * the cart can't be relied on to still line up by the time the request is built. One request
   * (rather than one `/cart/change.js` call per line) also means the total in the response —
   * and therefore what morphSection paints — always reflects every removed line at once, never
   * a stale in-between state from one call's response landing before a sibling call's.
   * @param {string[]} keys
   */
  #removeLines(keys) {
    const validKeys = keys.filter((key) => !!key);
    if (!validKeys.length) return;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const updates = Object.fromEntries(validKeys.map((key) => [key, 0]));
    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: validKeys.map((id) => ({ id, quantity: 0 })),
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          this.dispatchEvent(new CartErrorEvent({ error: parsedResponseText.errors, code: 'INVALID' }));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.__perfDisableT0 = performance.now(); // TEMP DIAGNOSTIC — Bug 2 busy-window duration
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    // ===== TEMP DIAGNOSTIC — Bug 2 (silently-swallowed clicks). REMOVE after measured. =====
    if (typeof this.__perfDisableT0 === 'number') {
      console.debug(
        `[cart-perf] cart locked (pointer-events:none) for ${(performance.now() - this.__perfDisableT0).toFixed(0)}ms` +
          `${this.isDrawer ? ' [drawer]' : ''}`
      );
      this.__perfDisableT0 = undefined;
    }
    // ===== END TEMP DIAGNOSTIC =====
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
